/**
 * Chart Abstraction List Export Modal
 * 
 * Allows users to select measures and encounter filter options before
 * generating an Excel workbook with patient data for chart abstraction.
 */

import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Download, 
  Loader2, 
  FileSpreadsheet, 
  Trash2,
  AlertCircle,
  Info
} from "lucide-react";
import { useChartAbstraction } from "@/contexts/ChartAbstractionContext";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import { useToast } from "@/components/ui/use-toast";
import { generateChartAbstractionWorkbook } from "@/lib/chart-abstraction-excel";

export type EncounterFilterType = 'incomplete' | 'met' | 'not_met' | 'exclusions';

interface MeasureRow {
  id: string;
  title: string;
  den?: number;
  met?: number;
  not?: number;
  ex?: number;
  exc?: number;
  ip?: number;
  isMultiStrata?: boolean;
  [key: string]: any;
}

interface ChartAbstractionExportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  practiceId: string;
  practiceName: string;
  measures: MeasureRow[];
  /** Active view lane — the export matches what's on screen (defaults GROUP) */
  evaluationType?: 'GROUP' | 'NPI';
  /** Selected provider when in NPI view */
  selectedNpi?: string;
}

const ENCOUNTER_FILTERS: { value: EncounterFilterType; label: string; description: string }[] = [
  { value: 'incomplete', label: 'Incomplete (needs documentation)', description: 'Patients with no numerator outcome recorded' },
  { value: 'not_met', label: 'Numerator Not Met', description: 'Patients who did not meet the measure' },
  { value: 'met', label: 'Numerator Met', description: 'Patients who met the measure' },
  { value: 'exclusions', label: 'Denominator Exclusions', description: 'Excluded patients' },
];

export function ChartAbstractionExportModal({
  open,
  onOpenChange,
  practiceId,
  practiceName,
  measures,
  evaluationType,
  selectedNpi,
}: ChartAbstractionExportModalProps) {
  const { toast } = useToast();
  const chartAbstraction = useChartAbstraction();
  const { programYear } = useProgramYear();
  
  // State - now supports multiple filter selection
  const [selectedMeasures, setSelectedMeasures] = useState<Set<string>>(new Set());
  const [selectedFilters, setSelectedFilters] = useState<Set<EncounterFilterType>>(new Set(['incomplete']));
  const [exporting, setExporting] = useState(false);
  
  // Get pending measures with their data
  const pendingMeasuresData = useMemo(() => {
    return chartAbstraction.pendingMeasures
      .map(measureId => {
        // Find the measure in the measures array
        const measure = measures.find(m => {
          const normalized = m.id.replace(/^0+/, '').padStart(3, '0');
          return normalized === measureId;
        });
        
        if (!measure) return null;
        
        return {
          measureId,
          measure,
          questions: chartAbstraction.getMeasureQuestions(measureId),
        };
      })
      .filter(Boolean) as { measureId: string; measure: MeasureRow; questions: string[] }[];
  }, [chartAbstraction.pendingMeasures, measures, chartAbstraction.getMeasureQuestions]);

  // Initialize selection when modal opens
  useEffect(() => {
    if (open) {
      // Select all pending measures by default
      setSelectedMeasures(new Set(chartAbstraction.pendingMeasures));
      // Default to incomplete filter
      setSelectedFilters(new Set(['incomplete']));
    }
  }, [open, chartAbstraction.pendingMeasures]);

  // Calculate patient counts based on selected filters
  const getPatientCount = (measure: MeasureRow, filters: Set<EncounterFilterType>): number => {
    let count = 0;
    
    if (filters.has('incomplete')) {
      const recorded = (measure.met || 0) + (measure.not || 0) + (measure.exc || 0);
      count += Math.max(0, (measure.den || 0) - recorded);
    }
    if (filters.has('met')) {
      count += measure.met || 0;
    }
    if (filters.has('not_met')) {
      count += measure.not || 0;
    }
    if (filters.has('exclusions')) {
      count += (measure.ex || 0) + (measure.exc || 0);
    }
    
    // If no filters selected, return 0
    return count;
  };

  // Total patient count
  const totalPatients = useMemo(() => {
    return pendingMeasuresData
      .filter(item => selectedMeasures.has(item.measureId))
      .reduce((sum, item) => sum + getPatientCount(item.measure, selectedFilters), 0);
  }, [pendingMeasuresData, selectedMeasures, selectedFilters]);

  // Handle filter toggle
  const handleFilterToggle = (filter: EncounterFilterType, checked: boolean) => {
    const newFilters = new Set(selectedFilters);
    if (checked) {
      newFilters.add(filter);
    } else {
      newFilters.delete(filter);
    }
    setSelectedFilters(newFilters);
  };

  // Handle select all measures
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedMeasures(new Set(chartAbstraction.pendingMeasures));
    } else {
      setSelectedMeasures(new Set());
    }
  };

  // Handle individual measure selection
  const handleMeasureSelect = (measureId: string, checked: boolean) => {
    const newSelected = new Set(selectedMeasures);
    if (checked) {
      newSelected.add(measureId);
    } else {
      newSelected.delete(measureId);
    }
    setSelectedMeasures(newSelected);
  };

  // Handle export
  const handleExport = async () => {
    if (selectedMeasures.size === 0 || selectedFilters.size === 0) return;
    
    setExporting(true);
    
    try {
      const selectedMeasuresList = pendingMeasuresData.filter(item => 
        selectedMeasures.has(item.measureId)
      );
      
      await generateChartAbstractionWorkbook({
        practiceId,
        practiceName,
        measures: selectedMeasuresList.map(item => ({
          measureId: item.measureId,
          title: item.measure.title,
          questions: item.questions,
          isMultiStrata: item.measure.isMultiStrata || false,
          submissionMethod: item.measure.submissionMethod,
        })),
        encounterFilters: Array.from(selectedFilters),
        year: programYear,
        evaluationType,
        npi: selectedNpi,
      });
      
      toast({
        title: "Export Successful",
        description: `Chart Abstraction List exported with ${selectedMeasures.size} measures`,
      });
      
      // Clear the pending list after successful export
      chartAbstraction.clearPendingList();
      onOpenChange(false);
      
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: error instanceof Error ? error.message : "Failed to generate workbook",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  // Handle clear list
  const handleClearList = () => {
    chartAbstraction.clearPendingList();
    setSelectedMeasures(new Set());
  };

  const allSelected = selectedMeasures.size === chartAbstraction.pendingMeasures.length && 
                      chartAbstraction.pendingMeasures.length > 0;
  const someSelected = selectedMeasures.size > 0 && !allSelected;

  // Generate filename preview
  const exportDate = new Date().toISOString().split('T')[0];
  const filename = `${practiceName} Chart Abstraction List ${exportDate}.xlsx`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Export Chart Abstraction List
          </DialogTitle>
          <DialogDescription>
            Select measures and encounter filters to generate an Excel workbook for chart review.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden space-y-4">
          {/* Practice Info */}
          <div className="text-sm space-y-1">
            <div><span className="text-muted-foreground">Practice:</span> <strong>{practiceName}</strong></div>
            <div><span className="text-muted-foreground">Export Date:</span> {exportDate}</div>
          </div>

          <Separator />

          {/* Encounter Filters - Multi-select */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Encounter Filters (select one or more)</Label>
            <div className="space-y-2">
              {ENCOUNTER_FILTERS.map(filter => (
                <div key={filter.value} className="flex items-center space-x-3">
                  <Checkbox
                    id={`filter-${filter.value}`}
                    checked={selectedFilters.has(filter.value)}
                    onCheckedChange={(checked) => handleFilterToggle(filter.value, checked === true)}
                  />
                  <Label htmlFor={`filter-${filter.value}`} className="flex-1 cursor-pointer">
                    <span className="font-medium">{filter.label}</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      ({filter.description})
                    </span>
                  </Label>
                </div>
              ))}
            </div>
            {selectedFilters.size === 0 && (
              <p className="text-xs text-destructive">Please select at least one filter</p>
            )}
          </div>

          <Separator />

          {/* Measure Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Select Measures to Export</Label>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="select-all"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) (el as any).indeterminate = someSelected;
                  }}
                  onCheckedChange={handleSelectAll}
                />
                <Label htmlFor="select-all" className="text-sm cursor-pointer">
                  Select All
                </Label>
              </div>
            </div>

            {pendingMeasuresData.length === 0 ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No measures have been added to the Chart Abstraction List. 
                  Use the Actions menu on each measure row to add measures.
                </AlertDescription>
              </Alert>
            ) : (
              <ScrollArea className="h-[200px] rounded-md border">
                <div className="p-2 space-y-1">
                  {pendingMeasuresData.map(item => {
                    const patientCount = getPatientCount(item.measure, selectedFilters);
                    return (
                      <div 
                        key={item.measureId}
                        className="flex items-center space-x-3 p-2 rounded hover:bg-muted/50"
                      >
                        <Checkbox
                          id={`measure-${item.measureId}`}
                          checked={selectedMeasures.has(item.measureId)}
                          onCheckedChange={(checked) => 
                            handleMeasureSelect(item.measureId, checked === true)
                          }
                        />
                        <Label 
                          htmlFor={`measure-${item.measureId}`}
                          className="flex-1 cursor-pointer text-sm"
                        >
                          <span className="font-medium">Measure {item.measureId}</span>
                          <span className="text-muted-foreground ml-2">
                            - {item.measure.title}
                          </span>
                          {item.measure.isMultiStrata && (
                            <Badge variant="outline" className="ml-2 text-xs">Multi-Strata</Badge>
                          )}
                        </Label>
                        <Badge variant="outline" className="text-xs">
                          {patientCount} patients
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Summary */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Selected: <strong>{selectedMeasures.size}</strong> measures | 
              <strong className="ml-1">{selectedFilters.size}</strong> filter(s) |
              <strong className="ml-1">{totalPatients}</strong> total patients
            </span>
          </div>

          {/* Filename Preview */}
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            <Info className="h-3 w-3" />
            Filename: {filename}
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleClearList}
            disabled={exporting || chartAbstraction.pendingCount === 0}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Clear List
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={exporting}>
            Cancel
          </Button>
          <Button 
            onClick={handleExport} 
            disabled={exporting || selectedMeasures.size === 0 || selectedFilters.size === 0}
          >
            {exporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Export
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
